import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  TrendingUp, 
  TrendingDown,
  Sparkles,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Target,
  PieChart,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Minus,
  Info,
  Lightbulb,
  ThumbsUp,
  ThumbsDown
} from "lucide-react";
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  PieChart as RechartsPie,
  Pie,
  Cell,
  ComposedChart,
  Line,
  Area
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const COLORS = ['#007AFF', '#34C759', '#FF9500', '#AF52DE', '#FF2D55', '#5856D6', '#00C7BE'];

const EbitEbitda = () => {
  const { getAuthHeaders } = useAuth();
  const [financialData, setFinancialData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [period, setPeriod] = useState("year");
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchData();
  }, [period, year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/financial/ebit-ebitda?period=${period}&year=${year}`,
        getAuthHeaders()
      );
      setFinancialData(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const runAIAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/financial/ebit-ebitda/analyze?period=${period}&year=${year}`,
        {},
        {
          ...getAuthHeaders(),
          timeout: 60000  // 60 seconds timeout for AI analysis
        }
      );
      setAiAnalysis(response.data.ai_analysis);
      setFinancialData(response.data.financial_data);
      toast.success("Analisi AI completata");
    } catch (error) {
      console.error("AI Analysis error:", error);
      toast.error("Errore nell'analisi AI: " + (error.message || "timeout"));
    } finally {
      setAnalyzing(false);
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case "eccellente": return "text-[#34C759]";
      case "buono": return "text-[#007AFF]";
      case "sufficiente": return "text-[#FF9500]";
      case "critico": return "text-[#FF3B30]";
      default: return "text-[#86868B]";
    }
  };

  const getHealthBgColor = (status) => {
    switch (status) {
      case "eccellente": return "bg-[#34C759]/10";
      case "buono": return "bg-[#007AFF]/10";
      case "sufficiente": return "bg-[#FF9500]/10";
      case "critico": return "bg-[#FF3B30]/10";
      default: return "bg-[#86868B]/10";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const metrics = financialData?.metrics || {};
  const revenue = financialData?.revenue || {};
  const costs = financialData?.costs || {};

  // Chart data
  const waterfallData = [
    { name: "Ricavi", value: revenue.total, fill: "#34C759" },
    { name: "Costi Op.", value: -costs.total_operating, fill: "#FF3B30" },
    { name: "EBITDA", value: metrics.ebitda, fill: "#007AFF" },
    { name: "Ammort.", value: -(financialData?.depreciation?.total || 0), fill: "#FF9500" },
    { name: "EBIT", value: metrics.ebit, fill: "#AF52DE" },
    { name: "Interessi", value: -(financialData?.interest?.expense || 0), fill: "#86868B" },
    { name: "Imposte", value: -(financialData?.taxes?.amount || 0), fill: "#FF2D55" },
    { name: "Utile Netto", value: metrics.net_income, fill: metrics.net_income >= 0 ? "#34C759" : "#FF3B30" }
  ];

  const costsPieData = Object.entries(costs.by_category || {}).map(([name, value]) => ({
    name,
    value
  }));

  const marginsData = [
    { name: "EBITDA Margin", value: metrics.ebitda_margin, benchmark: 20 },
    { name: "EBIT Margin", value: metrics.ebit_margin, benchmark: 12 },
    { name: "Net Margin", value: metrics.net_margin, benchmark: 8 }
  ];

  return (
    <div className="animate-fade-in" data-testid="ebit-ebitda-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[#007AFF]" />
            EBIT & EBITDA
          </h1>
          <p className="page-subtitle">Analisi redditività operativa con AI</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 h-11 rounded-xl border-[#D2D2D7]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
              <SelectItem value="year">Anno</SelectItem>
              <SelectItem value="quarter">Trimestre</SelectItem>
              <SelectItem value="month">Mese</SelectItem>
            </SelectContent>
          </Select>
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-28 h-11 rounded-xl border-[#D2D2D7]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
              {[2025, 2024, 2023].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={runAIAnalysis}
            disabled={analyzing}
            className="bg-gradient-to-r from-[#AF52DE] to-[#007AFF] hover:opacity-90 text-white rounded-full px-6"
            data-testid="analyze-ai-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analisi...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Analisi AI
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="stat-card card-hover bg-gradient-to-br from-[#34C759]/5 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">Ricavi Totali</p>
                <p className="text-2xl font-bold text-[#34C759]">{formatCurrency(revenue.total)}</p>
              </div>
              <div className="w-12 h-12 bg-[#34C759]/10 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#34C759]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover bg-gradient-to-br from-[#007AFF]/5 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">EBITDA</p>
                <p className={`text-2xl font-bold ${metrics.ebitda >= 0 ? 'text-[#007AFF]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(metrics.ebitda)}
                </p>
                <p className="text-sm text-[#86868B] mt-1">
                  Margin: {metrics.ebitda_margin}%
                </p>
              </div>
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-2xl flex items-center justify-center">
                <Target className="w-6 h-6 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover bg-gradient-to-br from-[#AF52DE]/5 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">EBIT</p>
                <p className={`text-2xl font-bold ${metrics.ebit >= 0 ? 'text-[#AF52DE]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(metrics.ebit)}
                </p>
                <p className="text-sm text-[#86868B] mt-1">
                  Margin: {metrics.ebit_margin}%
                </p>
              </div>
              <div className="w-12 h-12 bg-[#AF52DE]/10 rounded-2xl flex items-center justify-center">
                <PieChart className="w-6 h-6 text-[#AF52DE]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover bg-gradient-to-br from-[#FF9500]/5 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">Utile Netto</p>
                <p className={`text-2xl font-bold ${metrics.net_income >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(metrics.net_income)}
                </p>
                <p className="text-sm text-[#86868B] mt-1">
                  Net Margin: {metrics.net_margin}%
                </p>
              </div>
              <div className={`w-12 h-12 ${metrics.net_income >= 0 ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'} rounded-2xl flex items-center justify-center`}>
                {metrics.net_income >= 0 ? (
                  <ArrowUp className="w-6 h-6 text-[#34C759]" />
                ) : (
                  <ArrowDown className="w-6 h-6 text-[#FF3B30]" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Analysis Section */}
      {aiAnalysis && !aiAnalysis.error && (
        <Card className="bg-gradient-to-r from-[#AF52DE]/5 via-[#007AFF]/5 to-[#34C759]/5 rounded-2xl border border-[#AF52DE]/20 mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#AF52DE]" />
              Analisi AI
              {aiAnalysis.health_status && (
                <Badge className={`ml-2 ${getHealthBgColor(aiAnalysis.health_status)} ${getHealthColor(aiAnalysis.health_status)} border-0`}>
                  {aiAnalysis.health_status?.toUpperCase()}
                </Badge>
              )}
              {aiAnalysis.health_score && (
                <span className={`ml-2 text-2xl font-bold ${getHealthColor(aiAnalysis.health_status)}`}>
                  {aiAnalysis.health_score}/100
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Summary */}
            {aiAnalysis.summary && (
              <div className="mb-6 p-4 bg-white/50 rounded-xl">
                <p className="text-[#1D1D1F]">{aiAnalysis.summary}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Strengths */}
              {aiAnalysis.strengths?.length > 0 && (
                <div className="p-4 bg-[#34C759]/5 rounded-xl border border-[#34C759]/20">
                  <h4 className="font-semibold text-[#34C759] flex items-center gap-2 mb-3">
                    <ThumbsUp className="w-4 h-4" />
                    Punti di Forza
                  </h4>
                  <ul className="space-y-2">
                    {aiAnalysis.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1D1D1F]">
                        <CheckCircle2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Weaknesses */}
              {aiAnalysis.weaknesses?.length > 0 && (
                <div className="p-4 bg-[#FF9500]/5 rounded-xl border border-[#FF9500]/20">
                  <h4 className="font-semibold text-[#FF9500] flex items-center gap-2 mb-3">
                    <ThumbsDown className="w-4 h-4" />
                    Aree di Miglioramento
                  </h4>
                  <ul className="space-y-2">
                    {aiAnalysis.weaknesses.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#1D1D1F]">
                        <AlertTriangle className="w-4 h-4 text-[#FF9500] mt-0.5 flex-shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {aiAnalysis.recommendations?.length > 0 && (
              <div className="mt-6 p-4 bg-[#007AFF]/5 rounded-xl border border-[#007AFF]/20">
                <h4 className="font-semibold text-[#007AFF] flex items-center gap-2 mb-3">
                  <Lightbulb className="w-4 h-4" />
                  Raccomandazioni
                </h4>
                <ul className="space-y-2">
                  {aiAnalysis.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1D1D1F]">
                      <span className="w-6 h-6 bg-[#007AFF] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </span>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benchmark & Outlook */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {aiAnalysis.benchmark_comparison && (
                <div className="p-4 bg-white/50 rounded-xl">
                  <h4 className="font-semibold text-[#1D1D1F] flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4" />
                    Benchmark Settore
                  </h4>
                  <p className="text-sm text-[#86868B]">
                    {typeof aiAnalysis.benchmark_comparison === 'string' 
                      ? aiAnalysis.benchmark_comparison 
                      : aiAnalysis.benchmark_comparison.commentary || JSON.stringify(aiAnalysis.benchmark_comparison)}
                  </p>
                </div>
              )}

              {aiAnalysis.outlook && (
                <div className="p-4 bg-white/50 rounded-xl">
                  <h4 className="font-semibold text-[#1D1D1F] flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4" />
                    Prospettive
                  </h4>
                  <p className="text-sm text-[#86868B]">{aiAnalysis.outlook}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Waterfall Chart */}
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Dal Ricavo all'Utile Netto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={waterfallData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis type="number" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" width={80} fontSize={12} />
                  <Tooltip 
                    formatter={(value) => formatCurrency(Math.abs(value))}
                    contentStyle={{ 
                      background: 'white', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {waterfallData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Margins Comparison */}
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Margini vs Benchmark
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={marginsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis tickFormatter={(v) => `${v}%`} fontSize={12} />
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, name === 'value' ? 'Attuale' : 'Benchmark']}
                    contentStyle={{ 
                      background: 'white', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Attuale" fill="#007AFF" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="benchmark" name="Benchmark" stroke="#FF9500" strokeWidth={2} dot={{ fill: '#FF9500', r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Costs Breakdown & Revenue Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Costs by Category */}
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Costi per Categoria
            </CardTitle>
          </CardHeader>
          <CardContent>
            {costsPieData.length > 0 ? (
              <div className="flex items-center gap-6">
                <div className="h-[200px] w-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={costsPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {costsPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {costsPieData.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-[#1D1D1F]">{item.name}</span>
                      </div>
                      <span className="text-[#86868B] font-medium">{formatCurrency(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-[#86868B] py-8">Nessun dato disponibile</p>
            )}
          </CardContent>
        </Card>

        {/* Income Statement Summary */}
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
              Conto Economico Sintetico
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                <span className="text-[#1D1D1F]">Ricavi Servizi</span>
                <span className="font-semibold text-[#34C759]">{formatCurrency(revenue.services)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                <span className="text-[#1D1D1F]">Fatture Incassate</span>
                <span className="font-semibold text-[#34C759]">{formatCurrency(revenue.invoices)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                <span className="text-[#1D1D1F]">Altri Ricavi</span>
                <span className="font-semibold text-[#34C759]">{formatCurrency(revenue.other)}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-[#34C759]/5 rounded-lg px-3 -mx-3">
                <span className="font-semibold text-[#1D1D1F]">RICAVI TOTALI</span>
                <span className="font-bold text-[#34C759]">{formatCurrency(revenue.total)}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5] mt-4">
                <span className="text-[#1D1D1F]">Costi Operativi</span>
                <span className="font-semibold text-[#FF3B30]">-{formatCurrency(costs.total_operating)}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-[#007AFF]/5 rounded-lg px-3 -mx-3">
                <span className="font-semibold text-[#1D1D1F]">EBITDA</span>
                <span className={`font-bold ${metrics.ebitda >= 0 ? 'text-[#007AFF]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(metrics.ebitda)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                <span className="text-[#1D1D1F]">Ammortamenti</span>
                <span className="font-semibold text-[#FF9500]">-{formatCurrency(financialData?.depreciation?.total || 0)}</span>
              </div>
              <div className="flex justify-between items-center py-2 bg-[#AF52DE]/5 rounded-lg px-3 -mx-3">
                <span className="font-semibold text-[#1D1D1F]">EBIT</span>
                <span className={`font-bold ${metrics.ebit >= 0 ? 'text-[#AF52DE]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(metrics.ebit)}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                <span className="text-[#1D1D1F]">Interessi ({financialData?.interest?.rate}%)</span>
                <span className="font-semibold text-[#86868B]">-{formatCurrency(financialData?.interest?.expense || 0)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-[#E5E5E5]">
                <span className="text-[#1D1D1F]">Imposte ({financialData?.taxes?.rate}%)</span>
                <span className="font-semibold text-[#86868B]">-{formatCurrency(financialData?.taxes?.amount || 0)}</span>
              </div>
              <div className={`flex justify-between items-center py-2 ${metrics.net_income >= 0 ? 'bg-[#34C759]/5' : 'bg-[#FF3B30]/5'} rounded-lg px-3 -mx-3`}>
                <span className="font-semibold text-[#1D1D1F]">UTILE NETTO</span>
                <span className={`font-bold ${metrics.net_income >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                  {formatCurrency(metrics.net_income)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Sources Info */}
      <Card className="bg-[#F5F5F7] rounded-2xl border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-[#86868B]" />
            <span className="text-sm font-medium text-[#86868B]">Fonti Dati Utilizzate</span>
          </div>
          <div className="flex flex-wrap gap-4">
            {financialData?.data_sources && Object.entries(financialData.data_sources).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-[#86868B]">
                {key.replace(/_/g, ' ')}: {value}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EbitEbitda;
